import Data from 'data';
import Locale from 'data/locale';

import { Color } from "common";
import { colored } from "tooltips/builder";
import { formatNumber } from 'utils/format';

const autocolor = value => (value < 0 ? Color.RED : (value > 0 ? Color.BLUE : Color.WHITE));
const colorstat = (value, plus, percent) => colored(
  (plus && value > 0 ? '+' : '') + formatNumber(value || 0, 0, 10000) + (percent ? '%' : ''), autocolor(value));

const dr_chance = (...values) => 100 * (1 - values.reduce((tot, cur) => tot * (1 - 0.01 * cur), 1));
const capped = (value, min, max) => Math.max(min, Math.min(max, value));

function monsterStats(monster) {
  if (!monster) return null;
  let name = monster.name;
  switch (monster.type) {
  case 'champion':
    name = colored(name, Color.BLUE);
    break;
  case 'unique':
  case 'super':
  case 'boss':
    name = colored(name, Color.GOLD);
  // no default
  }
  return {name, ac: monster.base.armorclass, ar: monster.weapon1.display_tohit};
}

const attributesGroup = {
  name: 'Attributes',
  stats: [
    {
      name: 'Strength',
      collapse: true,
      baseValue: stats => (stats.base.sources.strength?.base || 0) + (stats.base.sources.strength?.stats || 0),
      value: stats => stats.base.strength,
      color: stats => stats.base.strength < stats.reqstr ? Color.RED : -1,
      tooltip: [
        '* Increases damage with strength based weapons and skills',
        '* Required strength: {0}{1!}',
        '* Increased by: {sources:base.strength}',
      ],
      tooltipValues: stats => [colored(stats.reqstr, stats.base.strength < stats.reqstr ? Color.RED : (stats.reqstr ? Color.WHITE : Color.GRAY)), stats.reqstr],
    },
    {
      name: 'Dexterity',
      collapse: true,
      baseValue: stats => (stats.base.sources.dexterity?.base || 0) + (stats.base.sources.dexterity?.stats || 0),
      value: stats => stats.base.dexterity,
      color: stats => stats.base.dexterity < stats.reqdex ? Color.RED : -1,
      tooltip: [
        '* Increases damage with dexterity based weapons and skills',
        '* Increases accuracy, defense and chance to block with shields',
        '* Required dexterity: {0}{1!}',
        '* Increased by: {sources:base.dexterity}',
      ],
      tooltipValues: stats => [colored(stats.reqdex, stats.base.dexterity < stats.reqdex ? Color.RED : (stats.reqdex ? Color.WHITE : Color.GRAY)), stats.reqdex],
    },
    {
      name: 'Vitality',
      baseValue: stats => (stats.base.sources.vitality?.base || 0) + (stats.base.sources.vitality?.stats || 0),
      value: stats => stats.base.vitality,
      tooltip: [
        '* Increases your maximum health and stamina',
        '* Chance to receive double life from potions: {0}',
        '* Increased by: {sources:base.vitality}',
      ],
      tooltipValues: stats => [colored(stats.doubleheal.toFixed(0) + '%', Color.BLUE)],
      filter: stats => stats.unit === 'player',
    },
    {
      name: 'Energy',
      baseValue: stats => (stats.base.sources.energy?.base || 0) + (stats.base.sources.energy?.stats || 0),
      value: stats => stats.base.energy,
      tooltip: [
        '* Increases your maximum mana',
        '* Chance to receive double mana from potions: {0}',
        '* Increased by: {sources:base.energy}',
      ],
      tooltipValues: stats => [colored(stats.doublemana.toFixed(0) + '%', Color.BLUE)],
      filter: stats => stats.unit === 'player',
    },
    {
      separator: true,
    },
    {
      name: 'Level',
      value: stats => stats.level,
      filter: stats => stats.unit !== 'player' && stats.unit !== 'mercenary',
    },
    {
      name: 'Life',
      value: stats => stats.base.maxhp,
      tooltip: [
        '* Represents the amount of damage your character can endure before dying',
        '* Increased by: {sources:base.maxhp}',
      ],
    },
    {
      name: 'Replenish Life',
      value: stats => stats.base.hpregen,
      collapse: true,
      plus: true,
      tooltip: [
        '* Regenerates {0} life per second when not taking damage',
        '* Increased by: {sources:base.hpregen}',
      ],
      tooltipValues: stats => [colored((stats.base.hpregen * 25 / 256).toFixed(1), Color.BLUE)],
      filter: stats => stats.unit === 'player',
    },
    {
      name: 'Life Regeneration',
      value: stats => stats.base.hpregen * 25 / 256,
      collapse: true,
      decimal: 1,
      tooltip: [
        '* Regenerates life when not taking damage',
      ],
      filter: stats => stats.unit !== 'player',
    },
    {
      name: 'Mana',
      value: stats => stats.base.maxmana,
      tooltip: [
        '* Primary resource for casting skills',
        '* Increased by: {sources:base.maxmana}',
      ],
      filter: stats => stats.unit === 'player',
    },
    {
      name: 'Mana Regeneration',
      value: stats => stats.manaregen * 25 / 256,
      decimal: 1,
      tooltip: [
        '* Mana recovered every second',
        '* Regenerate Mana bonus: {0}{sources:base.manarecoverybonus:0.0%}',
      ],
      tooltipValues: stats => [colorstat(stats.base.manarecoverybonus, true, true)],
      filter: stats => stats.unit === 'player',
    },
    {
      name: 'Experience',
      collapse: true,
      value: stats => stats.base.experience,
      tooltip: [
        '* Experience awarded on kill'
      ],
      filter: stats => stats.unit === 'monster',
    },
  ],
};

const defensesGroup = {
  name: 'Defenses',
  stats: [
    {
      name: 'Defense',
      value: stats => stats.base.armorclass,
      tooltip: [
        '* Reflects your ability to dodge in combat',
        '* Chance to be hit by {0}: {1}{2!}',
        '* Increased by: {sources:base.armorclass}',
      ],
      //tooltipValues: stats => [stats.level, colored(stats.monster_tohit.toFixed() + '%', Color.BLUE)]
      tooltipValues: (stats, allStats) => {
        const monster = stats.unit === 'monster' ? allStats.player : allStats.monster
        const monstat = monsterStats(monster);
        const ar = monstat?.ar;
        let ac = stats.base.armorclass || 0;
        if (monster) {
          let ftac = monster.weapon1.item_fractionaltargetac || 0;
          if (stats.type === 'super' || stats.type === 'boss' || stats.unit === 'player' || stats.unit === 'mercenary') {
            ftac = (ftac / 2) | 0;
          }
          ac -= (ac * ftac / 100) | 0;
          if (monster.weapon1.item_ignoretargetac && (stats.type === 'normal' || stats.type === 'dummy')) {
            ac = 0;
          }
        }
        const level = monster?.level || 0;
        return [
          monstat?.name,
          capped(200 * ar / (ar + (ac || 0)) * level / (level + (stats.level || 0)), 5, 95).toFixed() + '%',
          !!monster,
        ];
      },
    },
    {
      name: 'Defense vs Missile',
      value: stats => stats.base.armorclass_vs_missile,
      collapse: true,
      plus: true,
      tooltip: [
        '* Additional defense against missile attacks',
        '* Increased by: {sources:base.armorclass_vs_missile}',
      ],
    },
    {
      name: 'Defense vs Melee',
      value: stats => stats.base.armorclass_vs_hth,
      collapse: true,
      plus: true,
      tooltip: [
        '* Additional defense against melee attacks',
        '* Increased by: {sources:base.armorclass_vs_hth}',
      ],
    },
    {
      name: 'Block Chance',
      value: stats => stats.blockchance,
      collapse: true,
      percent: true,
      tooltip: [
        '* Determines your chance to defend against physical melee and ranged attacks',
        '* Increased by dexterity relative to character level',
        '* Base block chance: {0}{sources:base.toblock}',
      ],
      tooltipValues: stats => [stats.base.toblock + '%'],
    },
    {
      name: 'Weapon Block',
      value: stats => stats.dualwield && stats.base.passive_weaponblock,
      collapse: true,
      percent: true,
      tooltip: [
        '* Assassin can block attacks while using two claws',
      ],
    },
    {
      name: 'Block Rate',
      value: stats => stats.base.item_fasterblockrate,
      plus: true,
      percent: true,
      tooltip: [
        '* Reduces block animation length',
        '* Increased by: {sources:base.item_fasterblockrate}',
        '* Breakpoint: {table:fbr}Frames|FBR{/table} frames',
      ],
      filter: stats => stats.blockchance || (stats.dualwield && stats.base.weaponblock),
    },
    {
      name: 'Hit Recovery',
      value: stats => stats.base.item_fastergethitrate,
      collapse: false,
      plus: true,
      percent: true,
      tooltip: [
        '* Reduces animation length of getting hit',
        '* Increased by: {sources:base.item_fastergethitrate}',
        '* Breakpoint: {table:fhr}Frames|FHR{/table} frames',
      ],
    },
    {
      name: 'Thorns',
      value: stats => stats.base.item_attackertakesdamage,
      collapse: true,
      tooltip: [
        '* Deals physical damage when hit in melee',
        '* This damage can not be increased in any way besides reducing enemy physical resistances',
        '* Increased by: {sources:base.item_attackertakesdamage}',
      ],
    },
    {
      name: 'Lightning Thorns',
      value: stats => stats.base.item_attackertakeslightdamage,
      collapse: true,
      tooltip: [
        '* Deals {c9:Lightning} damage when hit in melee',
        '* This damage can not be increased in any way besides reducing enemy lightning resistances',
        '* Increased by: {sources:base.item_attackertakeslightdamage}',
      ],
    },
    {
      name: 'Damage Reduction',
      value: stats => stats.base.normal_damage_reduction,
      collapse: true,
      tooltip: [
        '* Reduces physical damage taken by a flat amount',
        '* This effect is applied before resistances',
        '* Increased by: {sources:base.normal_damage_reduction}',
      ],
    },
    {
      name: 'Magic Damage Reduction',
      value: stats => stats.base.magic_damage_reduction,
      collapse: true,
      tooltip: [
        '* Reduces magic and elemental damage taken by a flat amount',
        '* This effect is applied before resistances',
        '* Increased by: {sources:base.magic_damage_reduction}',
      ],
    },
    {
      separator: true,
    },
    {
      name: 'Fire Resistance',
      value: stats => stats.base.display_fireresist,
      color: stats => (stats.base.display_fireresist >= (stats.base.maxfireresist || 100) ? Color.GOLD : -1),
      percent: true,
      tooltip: [
        '* Uncapped resistance: {0}{1!}',
        '* Reduces {c1:Fire} damage taken',
        '* Increased by: {sources:base.display_fireresist}',
        '* Maximum resistance increased by: {sources:base.maxfireresist}',
      ],
      tooltipValues: stats => [colorstat(stats.full_fireresist, true, true), stats.full_fireresist >= stats.base.maxfireresist],
    },
    {
      name: 'Cold Resistance',
      value: stats => stats.base.display_coldresist,
      color: stats => (stats.base.display_coldresist >= (stats.base.maxcoldresist || 100) ? Color.GOLD : -1),
      percent: true,
      tooltip: [
        '* Uncapped resistance: {0}{1!}',
        '* Reduces {c3:Cold} damage taken',
        '* Increased by: {sources:base.display_coldresist}',
        '* Maximum resistance increased by: {sources:base.maxcoldresist}',
      ],
      tooltipValues: stats => [colorstat(stats.full_coldresist, true, true), stats.full_coldresist >= stats.base.maxcoldresist],
    },
    {
      name: 'Lightning Resistance',
      value: stats => stats.base.display_lightresist,
      color: stats => (stats.base.display_lightresist >= (stats.base.maxlightresist || 100) ? Color.GOLD : -1),
      percent: true,
      tooltip: [
        '* Uncapped resistance: {0}{1!}',
        '* Reduces {c9:Lightning} damage taken',
        '* Increased by: {sources:base.display_lightresist}',
        '* Maximum resistance increased by: {sources:base.maxlightresist}',
      ],
      tooltipValues: stats => [colorstat(stats.full_lightresist, true, true), stats.full_lightresist >= stats.base.maxlightresist],
    },
    {
      name: 'Poison Resistance',
      value: stats => stats.base.display_poisonresist,
      color: stats => (stats.base.display_poisonresist >= (stats.base.maxpoisonresist || 100) ? Color.GOLD : -1),
      percent: true,
      tooltip: [
        '* Uncapped resistance: {0}{1!}',
        '* Reduces {c2:Poison} damage taken',
        '* Increased by: {sources:base.display_poisonresist}',
        '* Maximum resistance increased by: {sources:base.maxpoisonresist}',
        '* Poison length resistance: {2}',
        '* Increased by: {sources:base.item_poisonlengthresist}',
      ],
      tooltipValues: stats => [
        colorstat(stats.full_poisonresist, true, true),
        stats.full_poisonresist >= stats.base.maxpoisonresist,
        colorstat(capped(stats.base.item_poisonlengthresist, -100, 75), false, true),
      ],
    },
    {
      name: 'Magic Resistance',
      collapse: true,
      value: stats => stats.base.display_magicresist,
      color: stats => (stats.base.display_magicresist >= (stats.base.maxmagicresist || 100) ? Color.GOLD : -1),
      percent: true,
      tooltip: [
        '* Uncapped resistance: {0}{1!}',
        '* Reduces {c8:Magic} (not elemental) damage taken',
        '* Increased by: {sources:base.display_magicresist}',
        '* Maximum resistance increased by: {sources:base.maxmagicresist}',
      ],
      tooltipValues: stats => [colorstat(stats.full_magicresist, true, true), stats.full_magicresist >= stats.base.maxmagicresist],
    },
    {
      name: 'Damage Reduction',
      collapse: true,
      value: stats => stats.base.display_damageresist,
      color: stats => (stats.base.display_damageresist >= (['player', 'follower'].includes(stats.unit) ? 50 : 100) ? Color.GOLD : -1),
      percent: true,
      tooltip: [
        '* Uncapped resistance: {0}{1!}',
        '* Reduces {c4:Physical} damage taken',
        '* Increased by: {sources:base.display_damageresist}',
      ],
      tooltipValues: stats => [colorstat(stats.full_damageresist, true, true), stats.full_damageresist >= (['player', 'follower'].includes(stats.unit) ? 50 : 100)],
    },
    {
      name: 'Curse Resistance',
      collapse: true,
      value: stats => stats.base.curse_resistance,
      color: stats => (stats.base.curse_resistance >= 100 ? Color.GOLD : -1),
      percent: true,
      tooltip: [
        '* Reduces Curses duration (also affects Shrines)',
      ],
    },
    {
      separator: true,
    },
    {
      name: 'Fire Absorb',
      collapse: true,
      value: stats => stats.base.item_absorbfire_percent,
      percent: true,
      tooltip: [
        '* Absorbs a portion of {c1:Fire} damage received and heals for the same amount',
        '* This effect is applied after resistances',
        '* Increased by: {sources:base.item_absorbfire_percent}'
      ],
    },
    {
      name: 'Fire Absorb',
      collapse: true,
      value: stats => stats.base.item_absorbfire,
      tooltip: [
        '* Absorbs a fixed amount of {c1:Fire} damage received and heals for the same amount',
        '* This effect is applied after resistances and percentage absorbs',
        '* Increased by: {sources:base.item_absorbfire}'
      ],
    },
    {
      name: 'Cold Absorb',
      collapse: true,
      value: stats => stats.base.item_absorbcold_percent,
      percent: true,
      tooltip: [
        '* Absorbs a portion of {c3:Cold} damage received and heals for the same amount',
        '* This effect is applied after resistances',
        '* Increased by: {sources:base.item_absorbcold_percent}'
      ],
    },
    {
      name: 'Cold Absorb',
      collapse: true,
      value: stats => stats.base.item_absorbcold,
      tooltip: [
        '* Absorbs a fixed amount of {c3:Cold} damage received and heals for the same amount',
        '* This effect is applied after resistances and percentage absorbs',
        '* Increased by: {sources:base.item_absorbcold}'
      ],
    },
    {
      name: 'Lightning Absorb',
      collapse: true,
      value: stats => stats.base.item_absorblight_percent,
      percent: true,
      tooltip: [
        '* Absorbs a portion of {c9:Lightning} damage received and heals for the same amount',
        '* This effect is applied after resistances',
        '* Increased by: {sources:base.item_absorblight_percent}'
      ],
    },
    {
      name: 'Lightning Absorb',
      collapse: true,
      value: stats => stats.base.item_absorblight,
      tooltip: [
        '* Absorbs a fixed amount of {c9:Lightning} damage received and heals for the same amount',
        '* This effect is applied after resistances and percentage absorbs',
        '* Increased by: {sources:base.item_absorblight}'
      ],
    },
    {
      name: 'Magic Absorb',
      collapse: true,
      value: stats => stats.base.item_absorbmagic_percent,
      percent: true,
      tooltip: [
        '* Absorbs a portion of {c8:Magic} (not elemental) damage received and heals for the same amount',
        '* This effect is applied after resistances',
        '* Increased by: {sources:base.item_absorbmagic_percent}'
      ],
    },
    {
      name: 'Magic Absorb',
      collapse: true,
      value: stats => stats.base.item_absorbmagic,
      tooltip: [
        '* Absorbs a fixed amount of {c8:Magic} (not elemental) damage received and heals for the same amount',
        '* This effect is applied after resistances and percentage absorbs',
        '* Increased by: {sources:base.item_absorbmagic}'
      ],
    },
    {
      separator: true,
    },
    {
      name: 'Drain Effectiveness',
      value: stats => stats.base.drain,
      percent: true,
      tooltip: [
        '* Determines effectiveness of Life and Mana Steal effects against the target'
      ],
      filter: stats => stats.base.drain !== 100,
    },
    {
      name: 'Chill Effectiveness',
      value: stats => -(stats.base.coldeffect || 0),
      percent: true,
      tooltip: [
        '* Animation speed reduction when target is chilled',
        '* Default value for players and normal monsters is 50%',
      ],
      filter: stats => stats.base.coldeffect !== -50,
    },
  ], 
};

const adventureGroup = {
  name: 'Adventure',
  stats: [
    {
      name: 'Stamina',
      value: stats => stats.base.maxstamina,
      tooltip: [
        '* Determines how far you can run before tiring out',
        '* Increased by: {sources:base.maxstamina}',
      ],
      filter: stats => stats.unit === 'player',
    },
    {
      name: 'Stamina Drain',
      value: stats => stats.staminadrain * 25 / 256,
      decimal: 1,
      tooltip: [
        '* Stamina drained per second while running',
        '* Can run for {0} seconds before stopping',
        '* Slower Stamina Drain: {1}{sources:base.item_staminadrainpct:0.0%}',
      ],
      tooltipValues: stats => [
        (256 * stats.base.maxstamina / (stats.staminadrain * 25)).toFixed(0),
        colorstat(stats.base.item_staminadrainpct, true, true),
      ],
      filter: stats => stats.unit === 'player',
    },
    {
      name: 'Stamina Regen',
      value: stats => stats.staminaregen * 25 / 256,
      decimal: 1,
      tooltip: [
        '* Stamina regenerated per second while standing still',
        '* Walking will regenerate stamina at 50% speed',
        '* Regenerates full stamina in {0} seconds',
        '* Heal Stamina Plus: {1}{sources:base.staminarecoverybonus:0.0%}',
      ],
      tooltipValues: stats => [
        (256 * stats.base.maxstamina / (stats.staminaregen * 25)).toFixed(0),
        colorstat(stats.base.staminarecoverybonus, true, true),
      ],
      filter: stats => stats.unit === 'player',
    },
    {
      name: 'Running Speed',
      value: stats => stats.runvelocity,
      decimal: 1,
      tooltip: [
        '* Walking Speed: {0}',
        '* Item bonus: {1}{sources:base.item_fastermovevelocity:0.0%}',
        '* Skill bonus: {2}{sources:base.velocitypercent:0.0%}',
      ],
      tooltipValues: stats => [
        stats.walkvelocity.toFixed(1),
        colorstat(stats.base.item_fastermovevelocity, true, true),
        colorstat(stats.base.velocitypercent, true, true),
      ],
      filter: stats => stats.unit === 'player',
    },
    {
      name: 'Speed',
      value: stats => stats.walkvelocity,
      decimal: 1,
      tooltip: [
        '* Item bonus: {0}{sources:base.item_fastermovevelocity:0.0%}',
        '* Skill bonus: {1}{sources:base.velocitypercent:0.0%}',
      ],
      tooltipValues: stats => [
        colorstat(stats.base.item_fastermovevelocity, true, true),
        colorstat(stats.base.velocitypercent, true, true),
      ],
      filter: stats => stats.unit !== 'player',
    },
    {
      name: 'Light Radius',
      value: stats => stats.base.item_lightradius,
      plus: true,
      collapse: true,
      tooltip: [
        '* Increased by: {sources:base.item_lightradius}',
      ],
      filter: stats => stats.unit === 'player',
    },
    {
      name: 'Magic Find',
      value: stats => stats.base.item_magicbonus,
      percent: true,
      collapse: true,
      tooltip: [
        '* Increases rarity of found items',
        '* Chance of finding {c3:Magic} items: {0}',
        '* Chance of finding {c9:Rare} items: {1}',
        '* Chance of finding {c2:Set} items: {2}',
        '* Chance of finding {c4:Unique} items: {3}',
        '* Increased by: {sources:base.item_magicbonus}',
      ],
      tooltipValues: stats => {
        const mf = stats.base.item_magicbonus || 0;
        return [
          colorstat(mf, true, true),
          colorstat(mf > 10 ? (mf * 600 / (mf + 600)) : mf, true, true),
          colorstat(mf > 10 ? (mf * 500 / (mf + 500)) : mf, true, true),
          colorstat(mf > 10 ? (mf * 250 / (mf + 250)) : mf, true, true),
        ];
      },
    },
    {
      name: 'Gold Find',
      value: stats => stats.base.item_goldbonus,
      percent: true,
      collapse: true,
      tooltip: [
        '* Increased by: {sources:base.item_goldbonus}',
      ],
    },
    {
      name: 'Experience',
      value: stats => stats.base.item_addexperience,
      percent: true,
      plus: true,
      collapse: true,
      tooltip: [
        '* Increased by: {sources:base.item_addexperience}',
      ],
    },
  ],
};

const spellsGroup = {
  name: 'Spells',
  stats: [
    {
      name: 'Cast Rate',
      value: stats => stats.base.item_fastercastrate,
      percent: true,
      plus: true,
      tooltip: [
        '* Reduces casting animation length',
        '* Increased by: {sources:base.item_fastercastrate}',
        '* Breakpoint: {table:fcr}Frames|FCR{/table} frames',
        '* {c1:Sorceress Lightning and Chain Lightning have different breakpoints}{0!}',
      ],
      tooltipValues: stats => stats.class === 'sor',
    },
    {
      name: 'Fire Damage',
      value: stats => stats.base.passive_fire_mastery,
      percent: true,
      plus: true,
      collapse: true,
      tooltip: [
        '* Increases {c1:Fire} damage done by attacks and spells',
        '* Increased by: {sources:base.passive_fire_mastery}',
      ],
    },
    {
      name: 'Fire Penetration',
      value: stats => stats.base.passive_fire_pierce,
      percent: true,
      collapse: true,
      tooltip: [
        '* Attacks and spells ignore a portion of enemy {c1:Fire} resistance',
        '* Does not affect {c1:Fire} immune enemies',
        '* Increased by: {sources:base.passive_fire_pierce}',
      ],
    },
    {
      name: 'Lightning Damage',
      value: stats => stats.base.passive_ltng_mastery,
      percent: true,
      plus: true,
      collapse: true,
      tooltip: [
        '* Increases {c9:Lightning} damage done by attacks and spells',
        '* Increased by: {sources:base.passive_ltng_mastery}',
      ],
    },
    {
      name: 'Lightning Penetration',
      value: stats => stats.base.passive_ltng_pierce,
      percent: true,
      collapse: true,
      tooltip: [
        '* Attacks and spells ignore a portion of enemy {c9:Lightning} resistance',
        '* Does not affect {c9:Lightning} immune enemies',
        '* Increased by: {sources:base.passive_ltng_pierce}',
      ],
    },
    {
      name: 'Cold Damage',
      value: stats => stats.base.passive_cold_mastery,
      percent: true,
      plus: true,
      collapse: true,
      tooltip: [
        '* Increases {c3:Cold} damage done by attacks and spells',
        '* Increased by: {sources:base.passive_cold_mastery}',
      ],
    },
    {
      name: 'Cold Penetration',
      value: stats => stats.base.passive_cold_pierce,
      percent: true,
      collapse: true,
      tooltip: [
        '* Attacks and spells ignore a portion of enemy {c3:Cold} resistance',
        '* Does not affect {c3:Cold} immune enemies',
        '* Increased by: {sources:base.passive_cold_pierce}',
      ],
    },
    {
      name: 'Poison Damage',
      value: stats => stats.base.passive_pois_mastery,
      percent: true,
      plus: true,
      collapse: true,
      tooltip: [
        '* Increases {c2:Poison} damage done by attacks and spells',
        '* Increased by: {sources:base.passive_pois_mastery}',
      ],
    },
    {
      name: 'Poison Penetration',
      value: stats => stats.base.passive_pois_pierce,
      percent: true,
      collapse: true,
      tooltip: [
        '* Attacks and spells ignore a portion of enemy {c2:Poison} resistance',
        '* Does not affect {c2:Poison} immune enemies',
        '* Increased by: {sources:base.passive_pois_pierce}',
      ],
    },
  ],
};

const makerange = (min, max, add=0) => (min >= max ? formatNumber(min + add, 2, 10000, true) : `${formatNumber(min + add, 2, 10000, true)}\u2013${formatNumber(max + add, 2, 10000, true)}`);
const poison = stats => {
  const len = (stats.poisonlength || 0) / (stats.poison_count || 1);
  if (!len) return 0;
  return makerange(Math.round((stats.poisonmindam || 0) * len / 256), Math.round((stats.poisonmaxdam || 0) * len / 256));
};
const poison2 = stats => {
  const len = (stats.poisonlength || 0) / (stats.poison_count || 1);
  if (!len) return 0;
  return makerange(Math.round((stats.display_poisonmindam || stats.poisonmindam || 0) * len / 256), Math.round((stats.display_poisonmaxdam || stats.poisonmaxdam || 0) * len / 256));
};
function max_index(...args) {
  let index = 0;
  for (let i = 1; i < args.length; ++i) {
    if (args[i] > args[index]) index = i;
  }
  return index;
}

const weaponGroup = {
  name: 'Weapon',
  stats: [
    {
      name: 'Range Adder',
      collapse: true,
      value: stats => stats.weapon1 !== stats.weapon2 && Data.items[stats.weapon1.baseitem]?.rangeadder,
    },
    {
      name: 'Attack Damage',
      collapse: true,
      value: stats => stats.weapon1.display_mindamage &&  ({min: stats.weapon1.display_mindamage || 0, max: stats.weapon1.display_maxdamage || 0}),
      color: ({ weapon1 }) => [Color.WHITE, Color.RED, Color.BLUE, Color.YELLOW, Color.GREEN, Color.ORANGE][max_index(
        (weapon1.display_physmindam || 0) + (weapon1.display_physmaxdam || 0),
        (weapon1.display_firemindam || weapon1.firemindam || 0) + (weapon1.display_firemaxdam || weapon1.firemaxdam || 0),
        (weapon1.display_coldmindam || weapon1.coldmindam || 0) + (weapon1.display_coldmaxdam || weapon1.coldmaxdam || 0),
        (weapon1.display_lightmindam || weapon1.lightmindam || 0) + (weapon1.display_lightmaxdam || weapon1.lightmaxdam || 0),
        ((weapon1.display_poisonmindam || weapon1.poisonmindam || 0) + (weapon1.display_poisonmaxdam || weapon1.poisonmaxdam || 0)) * ((weapon1.poisonlength || 0) / (weapon1.poison_count || 1)) / 256,
        (weapon1.display_magicmindam || weapon1.magicmindam || 0) + (weapon1.display_magicmaxdam || weapon1.magicmaxdam || 0))],
      tooltip: [
        '* Base damage: {0}{sources:weapon1.mindamage-maxdamage,weapon1.item_normaldamage}',
        '* Off-weapon increase: {1}{sources:weapon1.damagepercent,weapon1.passive_mastery_melee_dmg:%}',
        '* Fire damage: {2}{sources:weapon1.firemindam-firemaxdam:1}',
        '* Lightning damage: {3}{sources:weapon1.lightmindam-lightmaxdam:9}',
        '* Cold damage: {4}{sources:weapon1.coldmindam-coldmaxdam:3}',
        '* Magic damage: {5}{sources:weapon1.magicmindam-magicmaxdam:8}',
        '* Poison damage: {6} over {7} seconds{sources:weapon1.poisonmindam-poisonmaxdam/poisonlength:2}',
      ],
      tooltipValues: ({ weapon1 }) => [
        makerange(weapon1.mindamage || 0, weapon1.maxdamage || 0, weapon1.item_normaldamage || 0),
        colorstat((weapon1.damagepercent || 0) + (weapon1.passive_mastery_melee_dmg || 0), false, true),
        colored(makerange(weapon1.display_firemindam || weapon1.firemindam || 0, weapon1.display_firemaxdam || weapon1.firemaxdam || 0), Color.RED),
        colored(makerange(weapon1.display_lightmindam || weapon1.lightmindam || 0, weapon1.display_lightmaxdam || weapon1.lightmaxdam || 0), Color.YELLOW),
        colored(makerange(weapon1.display_coldmindam || weapon1.coldmindam || 0, weapon1.display_coldmaxdam || weapon1.coldmaxdam || 0), Color.BLUE),
        colored(makerange(weapon1.display_magicmindam || weapon1.magicmindam || 0, weapon1.display_magicmaxdam || weapon1.magicmaxdam || 0), Color.BLUE),
        colored(poison2(weapon1), Color.GREEN),
        parseFloat(((weapon1.poisonlength || 0) / (weapon1.poison_count || 1) / 25).toFixed(1)),
      ],
    },
    {
      name: 'Attack Rating',
      collapse: true,
      value: stats => stats.weapon1.display_tohit,
      tooltip: [
        '* Represents your ability to hit enemies in combat',
        '* Chance to hit {0}: {1}{5!}',
        '* Base accuracy: {2}{sources:weapon1.tohit}',
        '* Increased by: {3}{sources:weapon1.item_tohit_percent,weapon1.passive_mastery_melee_th,base.shrine_tohit_percent:%}',
        '* Target Defense is ignored (only vs normal monsters){sources:weapon1.item_ignoretargetac:b}',
        '* Target Defense reduced by: {4}{sources:weapon1.item_fractionaltargetac:%}'
      ],
      tooltipValues: (stats, allStats) => {
        const monster = stats.unit === 'monster' ? allStats.player : allStats.monster
        const monstat = monsterStats(monster);
        const ar = stats.weapon1.display_tohit;
        const ftac = stats.weapon1.item_fractionaltargetac || 0;
        let ac = monstat?.ac;
        if (monster) {
          let eftac = ftac;
          if (monster.type === 'super' || monster.type === 'boss' || monster.unit === 'player' || monster.unit === 'mercenary') {
            eftac = (eftac / 2) | 0;
          }
          ac -= (ac * eftac / 100) | 0;
          if (stats.weapon1.item_ignoretargetac && monster.type === 'normal') {
            ac = 0;
          }
        }
        return [
          monstat?.name,
          capped(200 * ar / (ar + (ac || 0)) * stats.level / (stats.level + (monster?.level || 0)), 5, 95).toFixed() + '%',
          stats.weapon1.tohit,
          colorstat((stats.weapon1.item_tohit_percent || 0) + (stats.weapon1.passive_mastery_melee_th || 0), true, true),
          colorstat(ftac, false, true),
          !!monster,
        ];
      },
    },
    {
      name: 'Critical Chance',
      collapse: true,
      value: stats => stats.weapon1.display_mindamage &&
        dr_chance(stats.weapon1.passive_mastery_melee_crit || 0, stats.weapon1.item_deadlystrike || 0, stats.base.passive_critical_strike || 0),
      percent: true,
      decimal: 0,
      tooltip: [
        '* Chance to double physical attack damage',
        '* Critical Strike: {sources:base.passive_critical_strike}',
        '* Deadly Strike: {sources:weapon1.item_deadlystrike}',
        '* Weapon Mastery: {sources:weapon1.passive_mastery_melee_crit}',
      ],
    },
    {
      name: 'Critical Chance (Monster)',
      collapse: true,
      value: stats => stats.base.crit,
      percent: true,
      decimal: 0,
      tooltip: [
        '* Chance to double attack damage, including elemental damage',
        '* Stacks with other sources of critical strike, potentially resulting in quadruple damage',
      ],
    },
    {
      name: 'Throw Damage',
      collapse: true,
      value: stats => stats.weapon1.display_throw_mindamage && ({min: stats.weapon1.display_throw_mindamage || 0, max: stats.weapon1.display_throw_maxdamage || 0}),
      color: ({ weapon1 }) => [Color.WHITE, Color.RED, Color.BLUE, Color.YELLOW, Color.GREEN, Color.ORANGE][max_index(
        (weapon1.display_throw_physmindam || 0) + (weapon1.display_throw_physmaxdam || 0),
        (weapon1.firemindam || 0) + (weapon1.firemaxdam || 0),
        (weapon1.coldmindam || 0) + (weapon1.coldmaxdam || 0),
        (weapon1.lightmindam || 0) + (weapon1.lightmaxdam || 0),
        ((weapon1.poisonmindam || 0) + (weapon1.poisonmaxdam || 0)) * ((weapon1.poisonlength || 0) / (weapon1.poison_count || 1)) / 256,
        (weapon1.magicmindam || 0) + (weapon1.magicmaxdam || 0))],
      tooltip: [
        '* Base damage: {0}{sources:weapon1.item_throw_mindamage-item_throw_maxdamage,weapon1.item_normaldamage}',
        '* Off-weapon increase: {1}{sources:weapon1.damagepercent,weapon1.passive_mastery_melee_dmg:%}',
        '* Fire damage: {2}{sources:weapon1.firemindam-firemaxdam:1}',
        '* Lightning damage: {3}{sources:weapon1.lightmindam-lightmaxdam:9}',
        '* Cold damage: {4}{sources:weapon1.coldmindam-coldmaxdam:3}',
        '* Magic damage: {5}{sources:weapon1.magicmindam-magicmaxdam:8}',
        '* Poison damage: {6}{sources:weapon1.poisonmindam-poisonmaxdam/poisonlength:2}',
      ],
      tooltipValues: ({ weapon1 }) => [
        makerange(weapon1.item_throw_mindamage || 0, weapon1.item_throw_maxdamage || 0, weapon1.item_normaldamage || 0),
        colorstat((weapon1.damagepercent || 0) + (weapon1.passive_mastery_throw_dmg || 0), false, true),
        colored(makerange(weapon1.firemindam || 0, weapon1.firemaxdam || 0), Color.RED),
        colored(makerange(weapon1.lightmindam || 0, weapon1.lightmaxdam || 0), Color.YELLOW),
        colored(makerange(weapon1.coldmindam || 0, weapon1.coldmaxdam || 0), Color.BLUE),
        colored(makerange(weapon1.magicmindam || 0, weapon1.magicmaxdam || 0), Color.BLUE),
        colored(poison(weapon1), Color.GREEN),
      ],
    },
    {
      name: 'Throw Attack Rating',
      collapse: true,
      value: stats => stats.weapon1.display_throw_tohit,
      tooltip: [
        '* Represents your ability to hit enemies in combat',
        '* Chance to hit {0}: {1}{5!}',
        '* Base accuracy: {2}{sources:weapon1.tohit}',
        '* Increased by: {3}{sources:weapon1.item_tohit_percent,weapon1.passive_mastery_throw_th,base.shrine_tohit_percent:%}',
        '* Target Defense is ignored (only vs normal monsters){sources:weapon1.item_ignoretargetac:b}',
        '* Target Defense reduced by: {4}{sources:weapon1.item_fractionaltargetac:%}'
      ],
      tooltipValues: (stats, allStats) => {
        const monster = stats.unit === 'monster' ? allStats.player : allStats.monster
        const monstat = monsterStats(monster);
        const ar = stats.weapon1.display_throw_tohit;
        const ftac = stats.weapon1.item_fractionaltargetac || 0;
        let ac = monstat?.ac;
        if (monster) {
          ac -= (ac * (ftac >> (monster.type === 'super' || monster.type === 'boss' ? 1 : 0)) / 100) | 0;
          if (stats.weapon1.item_ignoretargetac && (stats.type === 'normal' || stats.type === 'dummy')) {
            ac = 0;
          }
        }
        return [
          monstat?.name,
          capped(200 * ar / (ar + (ac || 0)) * stats.level / (stats.level + (monster?.level || 0)), 5, 95).toFixed() + '%',
          stats.weapon1.tohit,
          colorstat((stats.weapon1.item_tohit_percent || 0) + (stats.weapon1.passive_mastery_throw_th || 0), true, true),
          colorstat(ftac, false, true),
          !!monster,
        ];
      },
    },
    {
      name: 'Throw Critical Chance',
      collapse: true,
      value: stats => stats.weapon1.display_throw_mindamage &&
        dr_chance(stats.weapon1.passive_mastery_throw_crit || 0, stats.weapon1.item_deadlystrike || 0, stats.base.passive_critical_strike || 0),
      percent: true,
      decimal: 0,
      tooltip: [
        '* Chance to double attack damage',
        '* Critical Strike: {sources:base.passive_critical_strike}',
        '* Deadly Strike: {sources:weapon1.item_deadlystrike}',
        '* Weapon Mastery: {sources:weapon1.passive_mastery_throw_crit}',
      ],
    },
    {
      name: 'Attack Speed',
      collapse: true,
      value: stats => stats.weapon1.display_attackrate,
      percent: true,
      tooltip: [
        '* Reduces attack animation length',
        '* Attack speed from items is subject to diminishing returns',
        '* Increased by: {sources:weapon1.display_attackrate}',
        '* Including attack speed from items: {sources:weapon1.item_fasterattackrate}',
      ],
    },
    {
      separator: true,
    },
    {
      name: 'Life Steal',
      collapse: true,
      value: stats => stats.weapon1.lifedrainmindam,
      percent: true,
      tooltip: [
        '* Returns a percentage of physical attack damage dealt as life',
        '* Increased by: {sources:weapon1.lifedrainmindam}',
      ],
    },
    {
      name: 'Mana Steal',
      collapse: true,
      value: stats => stats.weapon1.manadrainmindam,
      percent: true,
      tooltip: [
        '* Returns a percentage of physical attack damage dealt as mana',
        '* Increased by: {sources:weapon1.manadrainmindam}',
      ],
    },
    {
      name: 'Crushing Blow',
      collapse: true,
      value: stats => stats.weapon1.item_crushingblow,
      percent: true,
      tooltip: [
        '* Chance to reduces enemy life by a fixed percentage on every attack',
        '* Increased by: {sources:weapon1.item_crushingblow}',
      ],
    },
    {
      name: 'Open Wounds',
      collapse: true,
      value: stats => stats.weapon1.item_openwounds,
      percent: true,
      tooltip: [
        '* Chance to apply a bleed effect to enemies, causing them to lose life over time',
        '* Prevents life regeneration for the duration',
        '* Target loses {0} life per second over 8 seconds',
        '* Increased by: {sources:weapon1.item_openwounds}',
      ],
      tooltipValues: stats => {
        const stage = Math.min(Math.ceil(stats.level / 15), 4);
        const amount = 9 * (stage + 1) * stats.level + (31 - 135 * stage * (stage + 1) / 2);
        return [colored((amount * 25) >> 8, Color.RED)];
      },
    },
  ],
};

const statList = [
  attributesGroup,
  defensesGroup,
  weaponGroup,
  {...weaponGroup, name: 'Off-hand Weapon', weapon2: true},
  spellsGroup,
  adventureGroup,
];

export default statList;

const _Lc = Locale('ui-calculator');

function makerangeq(min, max, color, frames) {
  if (!min && !max) return null;
  return <>{colored(makerange(parseFloat((min || 0).toFixed(2)), parseFloat((max || 0).toFixed(2))), color)}{frames ? ' ' + _Lc`per second` : ''}</>;
}

export const damageStatList = {
  total: {
    name: 'Total Damage',
    color: stats => [Color.WHITE, Color.RED, Color.BLUE, Color.YELLOW, Color.GREEN, Color.ORANGE][max_index(
      (stats.display_physmindam || 0) + (stats.display_physmaxdam || 0),
      (stats.display_firemindam || 0) + (stats.display_firemaxdam || 0),
      (stats.display_coldmindam || 0) + (stats.display_coldmaxdam || 0),
      (stats.display_lightmindam || 0) + (stats.display_lightmaxdam || 0),
      (stats.display_poisonmindam || 0) + (stats.display_poisonmaxdam || 0),
      (stats.display_magicmindam || 0) + (stats.display_magicmaxdam || 0))],
    value: stats => ({min: stats.display_mindamage, max: stats.display_maxdamage}),
    tooltip: [
      'Physical Damage: {0?}',
      'Fire Damage: {1?}',
      'Cold Damage: {2?}',
      'Lightning Damage: {3?}',
      'Poison Damage: {4?} over {6} seconds',
      'Magic Damage: {5?}',
      'Duration: {6?} seconds',
    ],
    tooltipValues: stats => [
      makerangeq(stats.display_physmindam, stats.display_physmaxdam, Color.WHITE, stats.frames),
      makerangeq(stats.display_firemindam, stats.display_firemaxdam, Color.RED, stats.frames),
      makerangeq(stats.display_coldmindam, stats.display_coldmaxdam, Color.BLUE, stats.frames),
      makerangeq(stats.display_lightmindam, stats.display_lightmaxdam, Color.YELLOW, stats.frames),
      makerangeq(stats.display_poisonmindam, stats.display_poisonmaxdam, Color.GREEN),
      makerangeq(stats.display_magicmindam, stats.display_magicmaxdam, Color.ORANGE, stats.frames),
      stats.display_poisonlength && parseFloat((stats.display_poisonlength / 25).toFixed(1)),
      stats.frames && parseFloat((stats.frames / 25).toFixed(1)),
    ],
  },
  phys: {
    name: 'Physical Damage',
    color: () => Color.WHITE,
    value: stats => stats.display_physmaxdam && ({min: stats.display_physmindam, max: stats.display_physmaxdam}),
    tooltip: [
      '* Base Skill Damage: {0?}',
      '* Base Damage: {1}{sources:mindamage-maxdamage/256}',
      '* Off-weapon increase: {2}{sources:damagepercent:%}',
      '* Multiplied by:{sources:skill_physdamage:%}',
      '* Conversion: {3?}',
      '* Hits {4?} times',
      '* Enemy Physical Resistance: {5?}%',
    ],
    tooltipValues: stats => [
      stats.base_maxdamage && makerange((stats.base_mindamage || 0) >> 8, (stats.base_maxdamage || 0) >> 8),
      makerange((stats.mindamage || 0) >> 8, (stats.maxdamage || 0) >> 8),
      colorstat(stats.damagepercent, false, true),
      stats.conversion && colorstat(-stats.conversion, false, true),
      stats.ticks,
      stats.display_physresist,
    ],
  },
  fire: {
    name: 'Fire Damage',
    color: () => Color.RED,
    value: stats => stats.display_firemaxdam && ({min: stats.display_firemindam, max: stats.display_firemaxdam}),
    decimal: stats => stats?.frames ? 1 : 0,
    tooltip: [
      'Damage per frame:{0!}',
      'Damage per {1?} frames:',
      '{sources:firemindam-firemaxdam/256:1}',
      '* Multiplied by:{sources:skill_firedamage:%}',
      '* Duration: {2?} seconds',
      '* Hits {3?} times',
      '* Enemy Fire Resistance: {4?}%',
    ],
    tooltipValues: stats => [
      stats.frames > 0 || stats.frames === -1,
      stats.frames < -1 && -stats.frames,
      stats.frames > 0 && parseFloat((stats.frames / 25).toFixed(1)),
      stats.ticks,
      stats.display_fireresist,
    ],
  },
  cold: {
    name: 'Cold Damage',
    color: () => Color.BLUE,
    value: stats => stats.display_coldmaxdam && ({min: stats.display_coldmindam, max: stats.display_coldmaxdam}),
    decimal: stats => stats?.frames ? 1 : 0,
    tooltip: [
      'Damage per frame:{0!}',
      'Damage per {1?} frames:',
      '{sources:coldmindam-coldmaxdam/256:3}',
      '* Multiplied by:{sources:skill_colddamage:%}',
      '* Duration: {2?} seconds',
      '* Hits {3?} times',
      '* Enemy Cold Resistance: {4?}%',
    ],
    tooltipValues: stats => [
      stats.frames > 0 || stats.frames === -1,
      stats.frames < -1 && -stats.frames,
      stats.frames > 0 && parseFloat((stats.frames / 25).toFixed(1)),
      stats.ticks,
      stats.display_coldresist,
    ],
  },
  light: {
    name: 'Lightning Damage',
    color: () => Color.YELLOW,
    value: stats => stats.display_lightmaxdam && ({min: stats.display_lightmindam, max: stats.display_lightmaxdam}),
    decimal: stats => stats?.frames ? 1 : 0,
    tooltip: [
      'Damage per frame:{0!}',
      'Damage per {1?} frames:',
      '{sources:lightmindam-lightmaxdam/256:9}',
      '* Multiplied by:{sources:skill_lightdamage:%}',
      '* Duration: {2?} seconds',
      '* Hits {3?} times',
      '* Enemy Lightning Resistance: {4?}%',
    ],
    tooltipValues: stats => [
      stats.frames > 0 || stats.frames === -1,
      stats.frames < -1 && -stats.frames,
      stats.frames > 0 && parseFloat((stats.frames / 25).toFixed(1)),
      stats.ticks,
      stats.display_lightresist,
    ],
  },
  poison: {
    name: 'Poison Damage',
    color: () => Color.GREEN,
    value: stats => stats.display_poisonmaxdam && ({min: stats.display_poisonmindam, max: stats.display_poisonmaxdam}),
    tooltip: [
      '{sources:poisonmindam-poisonmaxdam/poisonlength:2}',
      '* Multiplied by:{sources:skill_poisondamage:%}',
      '* Duration: {0} seconds',
      '* Enemy Poison Resistance: {1?}%',
    ],
    tooltipValues: stats => [parseFloat(((stats.display_poisonlength || 0) / 25).toFixed(1)), stats.display_poisonresist],
  },
  magic: {
    name: 'Magic Damage',
    color: () => Color.ORANGE,
    value: stats => stats.display_magicmaxdam && ({min: stats.display_magicmindam, max: stats.display_magicmaxdam}),
    decimal: stats => stats?.frames ? 1 : 0,
    tooltip: [
      'Damage per frame:{0!}',
      'Damage per {1?} frames:',
      '{sources:magicmindam-magicmaxdam/256:8}',
      '* Multiplied by:{sources:skill_magicdamage:%}',
      '* Duration: {2?} seconds',
      '* Hits {3?} times',
      '* Enemy Magic Resistance: {4?}%',
    ],
    tooltipValues: stats => [
      stats.frames > 0 || stats.frames === -1,
      stats.frames < -1 && -stats.frames,
      stats.frames > 0 && parseFloat((stats.frames / 25).toFixed(1)),
      stats.ticks,
      stats.display_magicresist,
    ],
  },
  tohit: {
    name: 'Chance to Hit',
    color: () => Color.WHITE,
    value: stats => stats.display_hitchance,
    percent: true,
    tooltip: [
      '* Accuracy Rating: {0}{sources:tohit:v}',
      '* Increased by: {1}{sources:tohit_percent,tohit_shrine:%}',
      '* Target defense: {2}',
      '* Reduced by:{sources:display_armorclass_percent:%}',
    ],
    tooltipValues: stats => [stats.display_tohit, colorstat(stats.tohit_percent || 0, false, true), stats.display_armorclass],
  },
  blockchance: {
    name: 'Block Chance',
    color: () => Color.WHITE,
    value: stats => stats.display_blockchance,
    percent: true,
  },
};

export const dpsStatList = {
  entry: {
    name: 'Damage per Second',
    color: stats => [Color.WHITE, Color.RED, Color.BLUE, Color.YELLOW, Color.GREEN, Color.ORANGE][max_index(
      stats.physical, stats.fire, stats.cold, stats.lightning, stats.poison, stats.magic)],
    value: stats => stats.dps / 256,
    tooltip: [
      'Physical: {0?}',
      'Fire: {1?}',
      'Cold: {2?}',
      'Lightning: {3?}',
      'Poison: {4?}',
      'Magic: {5?}',
      'Critical Chance: {6?}',
      'Critical Chance (Monster): {7?}',
      'Uptime: {8?}',
      'Rate: {9} every {10?} frames',
      'Duration: {11?} frames',
    ],
    tooltipValues: stats => [
      stats.physical && colored(formatNumber(stats.physical / 256, 0, 10000), Color.WHITE),
      stats.fire && colored(formatNumber(stats.fire / 256, 0, 10000), Color.RED),
      stats.cold && colored(formatNumber(stats.cold / 256, 0, 10000), Color.BLUE),
      stats.lightning && colored(formatNumber(stats.lightning / 256, 0, 10000), Color.YELLOW),
      stats.poison && colored(formatNumber(stats.poison / 256, 0, 10000), Color.GREEN),
      stats.magic && colored(formatNumber(stats.magic / 256, 0, 10000), Color.ORANGE),
      stats.crit && (stats.crit * 100).toFixed(0) + '%',
      stats.monstercrit && (stats.monstercrit * 100).toFixed(0) + '%',
      stats.uptime && (stats.uptime * 100).toFixed(0) + '%',
      ((stats.count && stats.count !== 1) && (stats.chance && stats.chance < 1) && `${parseFloat(stats.count.toFixed(2))}\u00d7${(stats.chance * 100).toFixed(0)}%`) ||
        ((stats.count && stats.count !== 1) && parseFloat(stats.count.toFixed(2))) || (stats.chance && stats.chance < 1 && (stats.chance * 100).toFixed(0) + '%'),
      !stats.uptime && stats.interval,
      !stats.uptime && stats.duration,
    ],
  },
};
